import React from "react"
import {
  BooleanField,
  DateField,
  EditButton,
  FunctionField,
  Link,
  NumberField,
  SelectField,
  Show,
  Tab,
  TabbedShowLayout,
  TextField,
  useRecordContext,
} from "react-admin"
import { makeStyles } from "@material-ui/core"

import PhoneCallButton from "../PhoneCallButton"
import SendSMSButton from "../SendSMSButton"
import ClaimHistory from "../Claim/ClaimHistory"
import TicketPanel from "../Claim/TicketPanel"
import GeneratePOAButton from "../GeneratePOAButton"
import { FlexContainer } from "../../lib/components/Container"
import { ChipListObjects } from "../ChipList"
import DuplicateClaims from "../DuplicateClaims"
import GenerateFunderContract from "../GenerateFunderContract"
import BMWClaimFiles from "../BmwClaim/BmwClaimFiles"
import INSURANCE_OPTIONS from "../../lib/constants/insurance-providers"
import SendToLawyersButton from "../SendToLawyersButton"

const insuranceChoices = [
  ...INSURANCE_OPTIONS.map(opt => ({ id: opt.value, name: opt.label })),
  { id: "other", name: "Other" },
]

const useStyles = makeStyles(theme => ({
  actionButton: {
    marginRight: theme.spacing(1),
  },
  lastField: {
    marginBottom: theme.spacing(3),
  },
}))

interface ClaimActionsParams {
  data?: Claim
  basePath: string
}

function ClaimActions ({ data, basePath }: ClaimActionsParams) {
  const classes = useStyles()

  if (!data) {
    return null
  }

  return (
    <>
      <FlexContainer direction='rtl' p={0.3}>
        <EditButton
          // @ts-expect-error
          data={data}
          record={{ id: data.id }}
          basePath={basePath}
          className={classes.actionButton}
        />
        <PhoneCallButton claim={data} className={classes.actionButton} />
        <SendSMSButton claim={data} className={classes.actionButton} />
        <SendToLawyersButton
          claimData={data}
          className={classes.actionButton}
        />
        <DuplicateClaims ocClaim={data} selectedIds={[data.id]} isShow={true}/>
        <GeneratePOAButton claim={data} className={classes.actionButton}/>
        <GenerateFunderContract claim={data} className={classes.actionButton}/>
      </FlexContainer>
    </>
  )
}


function Aside ({ record }: { record?: Claim }) {
  if (!record) {
    return null
  }

  return (
    <TicketPanel
      userId={record.userId}
      claimId={record.id}
      claim={record}
      type="bmwVsv"
    />
  )
}


export default function BmwVsvClaimShow (props: Record<string, any>) {
  const classes = useStyles()

  return (
    <Show
      {...props}
      // @ts-expect-error TODO
      label='ShowClaim'
      // @ts-expect-error TODO
      actions={<ClaimActions {...props} />}
      style={{ flexGrow: 1 }}
      aside={<Aside />}
    >
      <TabbedShowLayout>
        <Tab label='Summary'>
          <BooleanField
            source='lead'
            label='Lead'
            addLabel={true}
          />
          <TextField label='VSV ID' source='vsvId' />
          <TextField label='Claim ID' source='id' />
          <TextField label='Law Firm' source='lawFirm' />
          <TextField label='Law Firm Case ID' source='lawFirmCaseId' />
          <FunctionField
            label='User ID'
            source='userId'
            render={(claim: any) =>
              <Link to={`/users/${claim.userId}/show`}>
                {claim.userId}
              </Link>
            }
          />
          <TextField label='User Email' source='userEmail' />
          <ChipListObjects source="tags" label="Tags" />
          <DateField
            source='workflowTriggeredOn'
            label='Workflow triggerred on'
            locales='sv-SE'
            showTime
          />
          <TextField label='Lead Source' source='leadSource' />
          <DateField
            source='userRegistrationTime'
            locales='sv-SE'
            showTime
          />
          <DateField
            source='createdTime'
            locales='sv-SE'
            showTime
          />
          <DateField
            source='updatedTime'
            locales='sv-SE'
            showTime
          />
          <SelectField
            source='claimActionStatus'
            choices={[
              { id: "active", name: "Active" },
              { id: "dead", name: "Dead" },
              { id: "below5k", name: "Below 5k"},
              { id: "onHold", name: "On Hold" },
              { id: "finished", name: "Finished" },
            ]}
          />
          <TextField source='notes' />
          <BooleanField
            source="hasUploadedPurchaseContract"
            label="KV Uploaded"
          />
          <BooleanField
            source="hasUploadedVehicleRegistrationDocument"
            label="FZS Uploaded"
          />
          <BooleanField
            source="hasUploadedSalesContract"
            label="VV Uploaded"
          />
          <BooleanField
            source="hasUploadedLatestMileage"
            label="KM Stand Uploaded"
          />
          <BooleanField
            source="poaUploaded"
            label="POA Page Uploaded"
          />
          <br />
        </Tab>
        <Tab label='Contact Info'>
          <TextField label='Claim First Name'
            source='firstName' />
          <TextField label='Claim Last Name'
            source='lastName' />
          <TextField label='Contract First Name'
            source='purchaseContractFirstName' />
          <TextField label='Contract Last Name'
            source='purchaseContractLastName' />
          <TextField label='Contract Company'
            source='purchaseContractCompanyName' />
          <TextField source='address' />
          <TextField source='city' />
          <TextField source='postcode' />
          <TextField source='phoneNumber' />
          <TextField
            label='IBAN'
            source='iban'
            className={classes.lastField}
          />
        </Tab>
        <Tab label='BMW Files'>
          <BMWClaimFiles />
        </Tab>
        <Tab label='BMW Car'>
          <SelectField
            label="Car Type"
            source="bmwCarData.carType"
            choices={[
              { id: "bmw", name: "BMW" },
              { id: "mini", name: "MINI" },
            ]}
          />
          <SelectField
            label="Vehicle Ownership"
            source="bmwCarData.ownership"
            choices={[
              { id: "owner", name: "Owner" },
              { id: "sold", name: "Sold" },
            ]}
          />
          <NumberField
            label='Current Mileage'
            source='bmwCarData.currentMileage'
          />
          <NumberField
            label='Mileage on Sales Contract'
            source='bmwCarData.salesMileage'
          />
        </Tab>
        <Tab label='Validation'>
          <TextField label='Contract First Name'
            source='purchaseContractFirstName' />
          <TextField label='Contract Last Name'
            source='purchaseContractLastName' />
          <TextField source='address' />
          <TextField source='city' />
          <TextField source='postcode' />
          <TextField source='phoneNumber' />

          <BooleanField
            source='differentAddress'
            label='Different Residential Address'
          />

          <TextField source='livingAddress' label="Residential Address"/>
          <TextField source='livingCity' label="Residential City"/>
          <TextField source='livingZipCode' label="Residential Postal Code"/>
          <FunctionField
            label="Residential Country"
            render={(record: any) => record?.country || "Österreich"}
          />

          <SelectField
            label="Vehicle Ownership"
            source="bmwCarData.ownership"
            choices={[
              { id: "owner", name: "Owner" },
              { id: "sold", name: "Sold" },
            ]}
          />
          <NumberField
            label='Current Mileage'
            source='bmwCarData.currentMileage'
          />
          <NumberField
            label='Mileage on Sales Contract'
            source='bmwCarData.salesMileage'
          />
          <BooleanField
            source='legalExpensesInsurance'
            label='Legal Expenses Insurance'
            addLabel={true}
          />
        </Tab>
        <Tab label='RSV Info'>
          <BooleanField
            source='legalExpensesInsurance'
            label='Legal Expenses Insurance'
            addLabel={true}
          />
          <SelectField
            source='insuranceProvider'
            choices={insuranceChoices}
          />
          <FunctionField
            label='Other Insurance Provider'
            render={(record: any) =>
              record?.insuranceProvider === "other"
                ? record?.insuranceProviderManual || ""
                : null
            }
          />
          <TextField source='legalExpensesInsurancePolicyNumber' />
        </Tab>
        <Tab label='History'>
          <ClaimHistory />
        </Tab>
      </TabbedShowLayout>
    </Show>
  )
}
